import { useRef, useState, useCallback } from 'react';

function buildNoise(ctx, seconds, brownFactor) {
  const sr  = ctx.sampleRate;
  const buf = ctx.createBuffer(2, sr * seconds, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let last = 0;
    for (let i = 0; i < d.length; i++) {
      const w = Math.random() * 2 - 1;
      d[i] = (last + brownFactor * w) / (1 + brownFactor);
      last  = d[i];
      d[i] *= 3.5;
    }
  }
  return buf;
}

export function useAmbientSound() {
  const [enabled, setEnabled] = useState(false);
  const ctxRef  = useRef(null);
  const gainRef = useRef(null);

  // Build the audio graph once — called synchronously inside the click handler
  // so the AudioContext is created within a user gesture (no autoplay block).
  const initIfNeeded = useCallback(() => {
    if (ctxRef.current) return;

    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;

    const makeLoop = (seconds, brown) => {
      const src  = ctx.createBufferSource();
      src.buffer = buildNoise(ctx, seconds, brown);
      src.loop   = true;
      return src;
    };

    // Layer 1 — deep river / low rumble  (brown, 60–500 Hz)
    const rumble = makeLoop(5, 0.015);
    const hp1 = ctx.createBiquadFilter();
    hp1.type  = 'highpass'; hp1.frequency.value = 60;
    const lp1 = ctx.createBiquadFilter();
    lp1.type  = 'lowpass';  lp1.frequency.value = 500; lp1.Q.value = 0.3;
    const g1  = ctx.createGain(); g1.gain.value = 0.55;
    rumble.connect(hp1); hp1.connect(lp1); lp1.connect(g1);

    // Layer 2 — rain surface / drizzle  (white, 1800–9000 Hz)
    const drizzle = makeLoop(3, 0.06);
    const hp2 = ctx.createBiquadFilter();
    hp2.type  = 'highpass'; hp2.frequency.value = 1800;
    const lp2 = ctx.createBiquadFilter();
    lp2.type  = 'lowpass';  lp2.frequency.value = 9000;
    const g2  = ctx.createGain(); g2.gain.value = 0.18;
    drizzle.connect(hp2); hp2.connect(lp2); lp2.connect(g2);

    // Layer 3 — mid rain / glass  (band-pass 800 Hz)
    const mid = makeLoop(4, 0.035);
    const bp  = ctx.createBiquadFilter();
    bp.type   = 'bandpass'; bp.frequency.value = 800; bp.Q.value = 0.6;
    const g3  = ctx.createGain(); g3.gain.value = 0.22;
    mid.connect(bp); bp.connect(g3);

    // Master — starts silent
    const master      = ctx.createGain();
    master.gain.value = 0;
    gainRef.current   = master;

    g1.connect(master); g2.connect(master); g3.connect(master);
    master.connect(ctx.destination);

    rumble.start(); drizzle.start(); mid.start();
  }, []);

  const toggle = useCallback(() => {
    initIfNeeded(); // synchronous — AudioContext created in user gesture ✓

    const ctx  = ctxRef.current;
    const gain = gainRef.current;

    // Resume if browser suspended the context (happens on some mobile browsers)
    if (ctx && ctx.state === 'suspended') ctx.resume();

    setEnabled((prev) => {
      const next = !prev;

      if (gain && ctx) {
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(
          next ? 0.42 : 0,
          now + (next ? 1.8 : 1.0)
        );
      }

      return next;
    });
  }, [initIfNeeded]);

  return { enabled, toggle };
}
